import {
    ACTION_HEX_FULL_ACTION_KEYS,
    ACTION_HEX_FULL_ACTION_POSITIONS,
    ACTION_HEX_POSITION_ACTIONS,
    ACTION_NAMES,
    ACTIONS, Room
} from '../../../Constants';
import { deactivateAllRooms, unlockedActivatedStationIndexes } from '../../TwoD/Common/StationUtilities';
import ShouldIChooseAdvertisement from './WeightedChoices/ShouldIChooseAdvertisement';
import ShouldIChooseGhost from './WeightedChoices/ShouldIChooseGhost';
import ShouldIChooseLodging from './WeightedChoices/ShouldIChooseLodging';
import ShouldIChooseRenovation from './WeightedChoices/ShouldIChooseRenovation';
import ShouldIChooseResearch from './WeightedChoices/ShouldIChooseResearch';
import ShouldIChooseStaff from './WeightedChoices/ShouldIChooseStaff';

// Settle message shown when a repeat Concur lands after the Action Hex is
// already locked. Mirrors the engine's own LOCKED-state copy (Waiting.js
// artificialLocked) so the suggestion surface reads consistently across
// the lock edge.
const ACTION_HEX_LOCKED_MESSAGE = 'Acknowledge Locked';

export default function artificialActionChooser(gamePlayerController, aiPackage) {
    // State-driven settle, keyed on action_hex_locked — the lock state
    // every client receives via the lock patch, not local click history.
    // Once the hex is locked the rotation is committed for this turn, so a
    // repeat Concur fired before the engine's turn_status -> SELECT_ACTION
    // patch lands must NOT re-run the chooser and re-emit a Rotate the
    // engine rejects as already-locked (Discord smoke 2026-06-07). Mirror
    // of the human rotate guard in GamePlayerAPI.pushActionHexPosition; the
    // typed Reject is the net, not the plan. Swapping the suggestion to the
    // locked message settles the control's presentation too.
    if (gamePlayerController.gamePlayerData.action_hex_locked) {
        aiPackage.success = true;
        aiPackage.addToMessage(ACTION_HEX_LOCKED_MESSAGE);
        return;
    }
    const stationIndexes = unlockedActivatedStationIndexes(gamePlayerController.gamePlayerData);
    if (stationIndexes.length) {
        let name = Room.NAMES[gamePlayerController.gamePlayerData.station[stationIndexes[0]]];
        aiPackage.addToMessage(`Deactivate ${name}`);
        if (aiPackage.execute) {
            deactivateAllRooms(gamePlayerController);
        }
        return;
    }

    const chooser = new LordBeanChooseActions(gamePlayerController, aiPackage);

    // aiPackage.success = true;
    // aiPackage.addToMessage(`You Choose.`);
}


class LordBeanChooseActions {

    constructor(gamePlayerController, aiPackage) {
        this._gamePlayerController = gamePlayerController;
        this._aiPackage = aiPackage;

        this.action_weights = [ -999, 1, 1, 1, 1, 1, 1 ];
        this.actionHexPosition = gamePlayerController.gamePlayerData.action_hex_position;
        this._primary_action = null;
        this._timer_start = performance.now();
        this.choose();
    }

    choose = () => {
        this._should_i_take_a_research_action();
        this._should_i_take_a_lodging_action();
        this._should_i_take_a_staff_action();
        this._should_i_take_a_renovation_action();
        this._should_i_take_a_ghost_action();
        this._should_i_take_an_advertisement_action();
        this._choose_weighted_action();
        this._state_your_intention();
        if (this._aiPackage.execute) {
            this._aiPackage.aiParent.setMemory(
                'currentPrimaryAction',
                this._primary_action
                );  // activate the primary room?
            this._set_the_action_hex();
        }
    };

    _choose_weighted_action = () => {
        const heaviest_weight = Math.max(...this.action_weights);
        const matching_actions = [];
        this.action_weights.forEach((weight, action_id) => {
            if (weight === heaviest_weight) {
                matching_actions.push(action_id);
            }
        });
        matching_actions.sort((action1, action2) => this._aiPackage.actionPreferences.indexOf(action1) - this._aiPackage.actionPreferences.indexOf(action2));
        const action = matching_actions[0];
        this._primary_action = action;
        const index = ACTION_HEX_FULL_ACTION_KEYS.indexOf(action);
        let position = ACTION_HEX_FULL_ACTION_POSITIONS[index];
        if (heaviest_weight < 5) {
            const left_adjacent = action > 1 ? action - 1 : 6;
            const right_adjacent = action < 5 ? action + 1 : 1;
            const left_weight = this.action_weights[left_adjacent];
            const right_weight = this.action_weights[right_adjacent];
            if (left_weight > 0 && left_weight > right_weight) {
                if (position < 11) {
                    position += 1;
                } else {
                    position = 0;
                }
            } else if (right_weight > 0 && right_weight >= left_weight) {
                if (position > 0) {
                    position -= 1;
                } else {
                    position = 11;
                }
            }
        }
        this.actionHexPosition = position;
        return position;
    };

    _state_your_intention = () => {
        const actions = ACTION_HEX_POSITION_ACTIONS[this.actionHexPosition];
        const action_names = [
            ACTION_NAMES[actions[0]],
            ACTION_NAMES[actions[1]]
        ];
        const message = `Choose ${action_names[0]} | ${action_names[1]}`;
        this._aiPackage.addToMessage(message);
    };

    _set_the_action_hex = () => {
        this._gamePlayerController.lockTheActionHex(this.actionHexPosition);
        this._gamePlayerController.gamePlayerApi.aiRotateAndLockActionHex(this.actionHexPosition);
    };


    _should_i_take_a_research_action = () => {
        let should_i = new ShouldIChooseResearch(this._gamePlayerController, this._aiPackage);
        const weight = should_i.getActionWeight();
        this.action_weights[ACTIONS.RESEARCH_ID] = weight;
        let question = 'What weight does Research get?';
        this._aiPackage.addProvenance(question, weight);
    };


    _should_i_take_a_lodging_action = () => {
        let should_i = new ShouldIChooseLodging(this._gamePlayerController, this._aiPackage);
        const weight = should_i.getActionWeight();
        this.action_weights[ACTIONS.LODGING_ID] = weight;
        let question = 'What weight does Lodging get?';
        this._aiPackage.addProvenance(question, weight);
    };

    _should_i_take_a_staff_action = () => {
        let should_i = new ShouldIChooseStaff(this._gamePlayerController, this._aiPackage);
        const weight = should_i.getActionWeight();
        this.action_weights[ACTIONS.ROBOT_ID] = weight;
        let question = 'What weight does Staff get?';
        this._aiPackage.addProvenance(question, weight);
    };

    _should_i_take_a_renovation_action = () => {
        let should_i = new ShouldIChooseRenovation(this._gamePlayerController, this._aiPackage);
        const weight = should_i.getActionWeight();
        this.action_weights[ACTIONS.RENOVATION_ID] = weight;
        let question = 'What weight does Renovation get?';
        this._aiPackage.addProvenance(question, weight);
    };

    _should_i_take_a_ghost_action = () => {
        let should_i = new ShouldIChooseGhost(this._gamePlayerController, this._aiPackage);
        const weight = should_i.getActionWeight();
        this.action_weights[ACTIONS.GHOST_ID] = weight;
        let question = 'What weight does Ghost get?';
        this._aiPackage.addProvenance(question, weight);
    };

    _should_i_take_an_advertisement_action = () => {
        let should_i = new ShouldIChooseAdvertisement(this._gamePlayerController, this._aiPackage);
        const weight = should_i.getActionWeight();
        this.action_weights[ACTIONS.ADVERTISEMENT_ID] = weight;
        let question = 'What weight does Advertisement get?';
        this._aiPackage.addProvenance(question, weight);
    }


}
